import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { CruiseScheduleDetails } from '../utils/cruiseTransportUtils'

const VIEW_ON_MAP_START_ICON = <LineMapIcon size="XS" />

const ScheduleCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(16)};
  width: 100%;
  padding: ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  @container chat-itinerary-canvas (min-width: ${rem(560)}) {
    align-items: center;
    flex-direction: row;
  }
`

const PortGroup = styled(Group)`
  min-width: 0;
`

const ScheduleConnector = styled(Group)`
  min-width: 0;

  @container chat-itinerary-canvas (min-width: ${rem(560)}) {
    align-items: center;
  }
`

const ScheduleMain = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(16)};
  align-items: center;
  width: 100%;
  min-width: 0;

  @container chat-itinerary-canvas (min-width: ${rem(560)}) {
    display: flex;
    flex: 1;
    width: auto;

    > ${PortGroup} {
      flex: 0 1 auto;
    }

    > ${ScheduleConnector} {
      flex: 1 1 ${rem(130)};
      min-width: ${rem(130)};
    }
  }
`

const ConnectorLine = styled.div`
  width: ${rem(96)};
  height: 1px;
  background: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset-inline-end: 0;
    top: 50%;
    width: ${rem(8)};
    height: ${rem(8)};
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    transform: translateY(-50%) rotate(45deg);
  }

  /* The logical border already mirrors the corner, so a counter-rotation points it towards inline-end */
  [dir="rtl"] &::after {
    transform: translateY(-50%) rotate(-45deg);
  }

  @container chat-itinerary-canvas (min-width: ${rem(560)}) {
    width: ${rem(130)};
  }
`

interface Props {
  scheduleDetails: CruiseScheduleDetails
  scheduleDurationLabel?: string
  scheduleDepartureDate?: string
  scheduleArrivalDate?: string
  mapLabel: string
  isMapDisabled: boolean
  onMapClick?: () => void
}

function ChatCruiseTransportScheduleCard({
  scheduleDetails,
  scheduleDurationLabel,
  scheduleDepartureDate,
  scheduleArrivalDate,
  mapLabel,
  isMapDisabled,
  onMapClick,
}: Props) {
  return (
    <ScheduleCard>
      <ScheduleMain>
        <PortGroup direction="horizontal" gap={8} verticalAlign="center">
          {scheduleDetails.departure.localTime && (
            <Heading variant="heading5" as="span">{scheduleDetails.departure.localTime}</Heading>
          )}
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="bold">{scheduleDetails.departure.port.name}</BodyText>
            {scheduleDepartureDate && <BodyText variant="small" colour="neutral-two">{scheduleDepartureDate}</BodyText>}
          </Group>
        </PortGroup>
        <ScheduleConnector direction="vertical" gap={4} horizontalAlign="center">
          {scheduleDurationLabel && <BodyText variant="small" colour="neutral-two">{scheduleDurationLabel}</BodyText>}
          <ConnectorLine />
        </ScheduleConnector>
        <PortGroup direction="horizontal" gap={8} verticalAlign="center">
          {scheduleDetails.arrival.localTime && (
            <Heading variant="heading5" as="span">{scheduleDetails.arrival.localTime}</Heading>
          )}
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="bold">{scheduleDetails.arrival.port.name}</BodyText>
            {scheduleArrivalDate && <BodyText variant="small" colour="neutral-two">{scheduleArrivalDate}</BodyText>}
          </Group>
        </PortGroup>
      </ScheduleMain>
      <AssistChip
        size="small"
        variant="outline"
        startIcon={VIEW_ON_MAP_START_ICON}
        disabled={isMapDisabled}
        onClick={onMapClick}
      >
        {mapLabel}
      </AssistChip>
    </ScheduleCard>
  )
}

export default ChatCruiseTransportScheduleCard
